import { Injectable, computed, signal } from '@angular/core';
import type { IntegrationProfileResponse } from '@core/api/model/integrationProfileResponse';
import type { SelectOption } from '@app/types';

/**
 * Global state for the currently selected integration profile.
 * Populated on app startup by the topbar profile selector.
 */
@Injectable({
  providedIn: 'root',
})
export class IntegrationProfileStateService {
  private readonly profilesSignal = signal<IntegrationProfileResponse[]>([]);
  private readonly selectedProfileIdSignal = signal<string | null>(null);
  private readonly profilesLoadingSignal = signal<boolean>(false);

  readonly profiles = this.profilesSignal.asReadonly();
  readonly selectedProfileId = this.selectedProfileIdSignal.asReadonly();
  readonly profilesLoading = this.profilesLoadingSignal.asReadonly();

  readonly profileOptions = computed<SelectOption[]>(() =>
    this.profilesSignal().map((p) => ({
      value: p.integrationProfileId,
      label: p.integrationProfileName,
    })),
  );

  readonly selectedProfile = computed<IntegrationProfileResponse | null>(() => {
    const id = this.selectedProfileIdSignal();
    if (id == null) return null;
    return this.profilesSignal().find((p) => p.integrationProfileId === id) ?? null;
  });

  setProfiles(profiles: IntegrationProfileResponse[]): void {
    this.profilesSignal.set(profiles);
    if (this.selectedProfileIdSignal() == null && profiles.length > 0) {
      const first = profiles[0];
      if (first != null) {
        this.selectedProfileIdSignal.set(first.integrationProfileId);
      }
    }
  }

  setSelectedProfile(integrationProfileId: string | null): void {
    this.selectedProfileIdSignal.set(integrationProfileId);
  }

  setProfilesLoading(loading: boolean): void {
    this.profilesLoadingSignal.set(loading);
  }
}
